import { A } from "@solidjs/router";
import { createMemo, createSignal, For, Show } from "solid-js";
import type {
	Persona,
	RelevancyQuestionnaire,
	Requirement,
	ServerBand,
} from "../../api/domain.ts";
import { relevancyStatus } from "../../api/domain.ts";
import { useApi } from "../../api/provider.tsx";
import { Async } from "../../components/Async.tsx";
import { DeleteButton, deleteColumn } from "../../components/DeleteButton.tsx";
import {
	Actions,
	Button,
	ChoiceGroup,
	FieldGroup,
	Form,
	NumberField,
	SelectField,
	TextField,
	Toggle,
} from "../../components/Form.tsx";
import { Modal } from "../../components/Modal.tsx";
import { Panel } from "../../components/Panel.tsx";
import { RelevancyMark } from "../../components/RelevancyMark.tsx";
import { Table } from "../../components/Table.tsx";
import { useI18n } from "../../i18n/provider.tsx";
import { createLoader, loadedValue } from "../../lib/async.ts";
import { useSession } from "../../session/provider.tsx";
import { CustomerSelect, OwnerTag } from "./CustomersPage.tsx";
import { formatMinimum, formatMoment } from "../../lib/format.ts";
import styles from "./admin.module.scss";
import { statusLabel } from "./questionnaire-status.ts";

const blankPersona = (): Persona => ({
	id: "",
	name: "",
	description: null,
	view: null,
	customer: null,
});

const blankRequirement = (persona: string): Requirement => ({
	id: "",
	persona,
	capability: "",
	minimum: { kind: "score", value: 3 },
	rationale: "",
	author: "",
	active: true,
	created_at: Date.now(),
});

const bands: ServerBand[] = [
	"missing_or_weak",
	"partial",
	"solid",
	"excellent",
];

/**
 * A relevancy questionnaire collects one persona's weights, so it belongs to
 * whoever the persona belongs to; there is nothing to pick.
 */
const blankRelevancyQuestionnaire = (persona: Persona): RelevancyQuestionnaire => ({
	id: "",
	persona: persona.id,
	author: "",
	capabilities: [],
	topics: [],
	active: true,
	created_at: Date.now(),
	received_at: null,
	answered_at: null,
	evaluated_at: null,
	customer: persona.customer,
});

/** The magic link a persona's recipient opens; the questionnaire's id is the token. */
export const relevancyLink = (origin: string, id: string): string =>
	`${origin}/r/${id}`;

/**
 * Personas side by side with their must-haves. The rationale is what makes a
 * knockout defensible, so the editor refuses to create a requirement without one.
 */
export const PersonasPage = () => {
	const api = useApi();
	const i18n = useI18n();

	const personas = createLoader(() =>
		api.list("personas", { lang: i18n.locale() }),
	);
	const requirements = createLoader(() => api.list("requirements"));
	const capabilities = createLoader(() =>
		api.list("capabilities", { lang: i18n.locale() }),
	);
	const views = createLoader(() => api.list("views", { lang: i18n.locale() }));
	const authors = createLoader(() => api.list("authors"));
	const relevancies = createLoader(() => api.list("relevancies"));
	const relevancyQuestionnaires = createLoader(() =>
		api.list("relevancy-questionnaires"),
	);
	const tree = createLoader(() => api.tree({ lang: i18n.locale() }));
	const session = useSession();
	const customers = createLoader(() =>
		session.canWrite() ? api.list("customers") : Promise.resolve({ ok: true as const, value: [] }),
	);
	const [personaDraft, setPersonaDraft] = createSignal<Persona | undefined>(
		undefined,
	);
	const [requirementDraft, setRequirementDraft] = createSignal<
		Requirement | undefined
	>(undefined);
	const [relevancyDraft, setRelevancyDraft] = createSignal<
		RelevancyQuestionnaire | undefined
	>(undefined);
	/** The scope as ticked, in topic ids; capabilities are derived server-side. */
	const [scope, setScope] = createSignal<string[]>([]);
	const [copied, setCopied] = createSignal("");

	const capabilityLabel = (id: string): string =>
		(loadedValue(capabilities.value()) ?? []).find((entry) => entry.id === id)
			?.label ?? id;

	const authorName = (id: string): string =>
		(loadedValue(authors.value()) ?? []).find((entry) => entry.id === id)
			?.name ?? id;

	const personaName = (id: string): string =>
		(loadedValue(personas.value()) ?? []).find((entry) => entry.id === id)
			?.name ?? id;

	const mine = createMemo(
		() => (persona: string) =>
			(loadedValue(requirements.value()) ?? []).filter(
				(entry) => entry.persona === persona,
			),
	);

	const ratingsOf = createMemo(
		() => (persona: string) =>
			(loadedValue(relevancies.value()) ?? []).filter(
				(entry) => entry.persona === persona,
			),
	);

	const bogenOf = createMemo(
		() => (persona: string) =>
			(loadedValue(relevancyQuestionnaires.value()) ?? []).filter(
				(entry) => entry.persona === persona,
			),
	);

	const origin = () => (typeof location === "undefined" ? "" : location.origin);

	const saveRelevancyQuestionnaire = async (
		questionnaire: RelevancyQuestionnaire,
	) => {
		const result = await api.create("relevancy-questionnaires", {
			...questionnaire,
			topics: scope(),
		});
		if (!result.ok) return;
		setRelevancyDraft(undefined);
		setScope([]);
		relevancyQuestionnaires.reload();
	};

	const acceptRelevancy = async (id: string) => {
		const result = await api.acceptRelevancy(id);
		if (!result.ok) return;
		relevancyQuestionnaires.reload();
		relevancies.reload();
	};

	const savePersona = async (persona: Persona) => {
		const result = await api.create("personas", persona);
		if (!result.ok) return;
		setPersonaDraft(undefined);
		personas.reload();
	};

	const saveRequirement = async (requirement: Requirement) => {
		if (requirement.rationale.trim() === "") return;
		const result = await api.create("requirements", requirement);
		if (!result.ok) return;
		setRequirementDraft(undefined);
		requirements.reload();
	};

	return (
		<>
			<div class={styles.heading}>
				<h1>{i18n.t().personas.title}</h1>
			</div>

			<Panel
				actions={
					<Button
						label={i18n.t().personas.newPersona}
						onClick={() => setPersonaDraft(blankPersona())}
					/>
				}
			>
				<Show when={personaDraft()}>
					{(persona) => (
						<Form>
							<TextField
								label={i18n.t().common.name}
								value={persona().name}
								onInput={(name) => setPersonaDraft({ ...persona(), name })}
							/>
							<TextField
								label={i18n.t().common.description}
								optional
								multiline
								value={persona().description ?? ""}
								onInput={(description) =>
									setPersonaDraft({
										...persona(),
										description: description === "" ? null : description,
									})
								}
							/>
							<SelectField
								label={i18n.t().personas.view}
								placeholder={i18n.t().common.none}
								optional
								choices={(loadedValue(views.value()) ?? []).map((entry) => ({
									id: entry.id,
									label: entry.label,
								}))}
								value={persona().view ?? ""}
								onPick={(view) =>
									setPersonaDraft({
										...persona(),
										view: view === "" ? null : view,
									})
								}
							/>
							<Show when={session.canWrite()}>
								<CustomerSelect
									customers={loadedValue(customers.value())}
									value={persona().customer}
									onPick={(customer) =>
										setPersonaDraft({ ...persona(), customer })
									}
								/>
							</Show>
							<Actions>
								<Button
									label={i18n.t().common.create}
									tone="primary"
									onClick={() => void savePersona(persona())}
								/>
								<Button
									label={i18n.t().common.cancel}
									onClick={() => setPersonaDraft(undefined)}
								/>
							</Actions>
						</Form>
					)}
				</Show>

				<Async value={personas.value()} onRetry={personas.reload}>
					{(rows) => (
						<div class={styles.rows}>
							<For each={rows}>
								{(persona) => (
									<Panel
										title={persona.name}
										description={persona.description ?? persona.id}
										actions={
											<>
												<Show when={!session.isCustomer()}>
													<OwnerTag
														customers={loadedValue(customers.value())}
														owner={persona.customer}
													/>
												</Show>
												<Button
													label={i18n.t().personas.newRequirement}
													onClick={() =>
														setRequirementDraft(blankRequirement(persona.id))
													}
												/>
												<DeleteButton
													collection="personas"
													id={persona.id}
													label={persona.name}
													onRemoved={() => {
														personas.reload();
														requirements.reload();
													}}
												/>
											</>
										}
									>
										<Table
											rows={mine()(persona.id)}
											rowKey={(requirement) => requirement.id}
											emptyLabel={i18n.t().common.empty}
											columns={[
												{
													key: "capability",
													header: i18n.t().common.capability,
													cell: (requirement) =>
														capabilityLabel(requirement.capability),
												},
												{
													key: "minimum",
													header: i18n.t().reports.minimum,
													cell: (requirement) =>
														formatMinimum(
															i18n.t(),
															requirement.minimum,
															i18n.locale(),
														),
												},
												{
													key: "rationale",
													header: i18n.t().personas.rationale,
													cell: (requirement) => (
														<span class={styles.rationale}>
															{requirement.rationale}
														</span>
													),
												},
												deleteColumn({
													header: i18n.t().common.actions,
													collection: "requirements",
													id: (requirement) => requirement.id,
													onRemoved: requirements.reload,
												}),
											]}
										/>

										{/*
                      Folded away by default: the ratings table runs to one row
                      per capability, which buries the requirements above it.
                    */}
										<Panel
											title={i18n.t().relevancy.title}
											collapsible
											count={bogenOf()(persona.id).length}
											actions={
												<Button
													label={i18n.t().relevancy.newQuestionnaire}
													onClick={() =>
														setRelevancyDraft(
															blankRelevancyQuestionnaire(persona),
														)
													}
												/>
											}
										>
											<Table
												rows={bogenOf()(persona.id)}
												rowKey={(entry) => entry.id}
												emptyLabel={i18n.t().relevancy.noQuestionnaires}
												columns={[
													{
														key: "id",
														header: i18n.t().relevancy.magicLink,
														cell: (entry) => (
															<span class={styles.mono}>
																{relevancyLink(origin(), entry.id)}
															</span>
														),
													},
													{
														key: "author",
														header: i18n.t().common.author,
														cell: (entry) => authorName(entry.author),
													},
													/*
													  Whose world the link belongs to. A customer user
													  only ever sees their own, so the column is theirs
													  to skip, as on the persona heading above.
													*/
													...(session.isCustomer()
														? []
														: [
																{
																	key: "customer",
																	header: i18n.t().common.customer,
																	cell: (entry: RelevancyQuestionnaire) => (
																		<OwnerTag
																			customers={loadedValue(customers.value())}
																			owner={entry.customer}
																		/>
																	),
																},
															]),
													{
														key: "status",
														header: i18n.t().common.status,
														cell: (entry) =>
															statusLabel(i18n.t(), relevancyStatus(entry)),
													},
													{
														key: "actions",
														header: i18n.t().common.actions,
														cell: (entry) => (
															<span class={styles.inline}>
																<Button
																	label={
																		copied() === entry.id
																			? i18n.t().common.copied
																			: i18n.t().relevancy.copyLink
																	}
																	onClick={() => {
																		void navigator.clipboard?.writeText(
																			relevancyLink(origin(), entry.id),
																		);
																		setCopied(entry.id);
																	}}
																/>
																{/*
                                  Gated exactly as a product questionnaire is:
                                  nothing to accept twice, and nothing to accept
                                  before the recipient has picked anything.
                                */}
																<Button
																	label={
																		entry.evaluated_at === null
																			? i18n.t().relevancy.accept
																			: i18n.t().relevancy.accepted
																	}
																	tone="primary"
																	disabled={
																		entry.evaluated_at !== null ||
																		entry.answered_at === null
																	}
																	onClick={() => void acceptRelevancy(entry.id)}
																/>
															</span>
														),
													},
												]}
											/>

											<Table
												rows={ratingsOf()(persona.id)}
												rowKey={(rating) => rating.id}
												emptyLabel={i18n.t().relevancy.noRatings}
												columns={[
													{
														key: "capability",
														header: i18n.t().common.capability,
														cell: (rating) =>
															capabilityLabel(rating.capability),
													},
													{
														/*
														  Step and weight are one fact seen twice, so they
														  share a cell: the mark's colour and position give
														  the step, its title spells out both.
														*/
														key: "weighting",
														header: i18n.t().relevancy.weighting,
														cell: (rating) => (
															<RelevancyMark rating={rating.rating} />
														),
													},
													{
														key: "note",
														header: i18n.t().relevancy.note,
														cell: (rating) => rating.note ?? "",
													},
													{
														key: "author",
														header: i18n.t().common.author,
														cell: (rating) => rating.author,
													},
													{
														key: "created",
														header: i18n.t().questionnaires.created,
														cell: (rating) =>
															formatMoment(rating.created_at, i18n.locale()),
													},
												]}
											/>
										</Panel>
									</Panel>
								)}
							</For>
						</div>
					)}
				</Async>
			</Panel>

			{/*
			  A dialog, not a panel under the list: the button sits inside a persona
			  deep down the page, and a form that appears below the fold reads as a
			  click that did nothing. Cancelling drops the draft and the ticked scope.
			*/}
			<Modal
				open={relevancyDraft() !== undefined}
				title={i18n.t().relevancy.newQuestionnaire}
				description={personaName(relevancyDraft()?.persona ?? "")}
				onClose={() => {
					setRelevancyDraft(undefined);
					setScope([]);
				}}
			>
				<Show when={relevancyDraft()}>
					{(questionnaire) => (
						<Async value={authors.value()} onRetry={authors.reload}>
								{(rows) => (
									<Form>
										<SelectField
											label={i18n.t().common.author}
											placeholder={i18n.t().common.none}
											choices={rows.map((author) => ({
												id: author.id,
												label: author.name,
											}))}
											value={questionnaire().author}
											onPick={(author) =>
												setRelevancyDraft({ ...questionnaire(), author })
											}
										/>
										<Show when={rows.length === 0}>
											<p class={styles.note}>
												{i18n.t().relevancy.noAuthors}{" "}
												<A href="/authors">{i18n.t().questionnaires.addAuthor}</A>
											</p>
										</Show>
										<FieldGroup
											label={i18n.t().relevancy.scope}
											hint={i18n.t().relevancy.scopeHint}
										>
											{/*
	                      Topics, not questions: the scope of a relevancy
	                      questionnaire is capabilities, and a capability belongs to
	                      exactly one topic. Nothing ticked falls back to the
	                      persona's view, which the server resolves.
	                    */}
											<Async value={tree.value()} onRetry={tree.reload}>
												{(branches) => (
													<ChoiceGroup
														name="relevancy-scope"
														legend={i18n.t().relevancy.scope}
														multiple
														choices={branches.map((branch) => ({
															id: branch.id,
															label: branch.title,
														}))}
														selected={scope()}
														onToggle={(id, checked) =>
															setScope(
																checked
																	? [
																			...scope().filter((entry) => entry !== id),
																			id,
																		]
																	: scope().filter((entry) => entry !== id),
															)
														}
													/>
												)}
											</Async>
										</FieldGroup>
										<Actions>
											<Button
												label={i18n.t().common.create}
												tone="primary"
												disabled={questionnaire().author === ""}
												onClick={() =>
													void saveRelevancyQuestionnaire(questionnaire())
												}
											/>
											<Button
												label={i18n.t().common.cancel}
												onClick={() => {
													setRelevancyDraft(undefined);
													setScope([]);
												}}
											/>
										</Actions>
									</Form>
								)}
							</Async>
					)}
				</Show>
			</Modal>

			<Show when={requirementDraft()}>
				{(requirement) => (
					<Panel
						title={i18n.t().personas.newRequirement}
						description={requirement().persona}
					>
						<Async value={capabilities.value()} onRetry={capabilities.reload}>
							{(rows) => (
								<Form>
									<SelectField
										label={i18n.t().common.capability}
										placeholder={i18n.t().common.none}
										choices={rows.map((entry) => ({
											id: entry.id,
											label: entry.label,
										}))}
										value={requirement().capability}
										onPick={(capability) =>
											setRequirementDraft({ ...requirement(), capability })
										}
									/>
									<SelectField
										label={i18n.t().personas.minimumKind}
										choices={[
											{ id: "score", label: i18n.t().personas.minimumScore },
											{ id: "band", label: i18n.t().personas.minimumBand },
										]}
										value={requirement().minimum.kind}
										onPick={(kind) =>
											setRequirementDraft({
												...requirement(),
												minimum:
													kind === "band"
														? { kind: "band", value: "solid" }
														: { kind: "score", value: 3 },
											})
										}
									/>
									<Show when={requirement().minimum.kind === "score"}>
										<NumberField
											label={i18n.t().personas.minimumScore}
											min={0}
											max={5}
											value={
												requirement().minimum.kind === "score"
													? (requirement().minimum.value as number)
													: 3
											}
											onInput={(value) =>
												setRequirementDraft({
													...requirement(),
													minimum: { kind: "score", value },
												})
											}
										/>
									</Show>
									<Show when={requirement().minimum.kind === "band"}>
										<SelectField
											label={i18n.t().personas.minimumBand}
											choices={bands.map((band) => ({ id: band, label: band }))}
											value={
												requirement().minimum.kind === "band"
													? (requirement().minimum.value as string)
													: "solid"
											}
											onPick={(value) =>
												setRequirementDraft({
													...requirement(),
													minimum: { kind: "band", value: value as ServerBand },
												})
											}
										/>
									</Show>
									<TextField
										label={i18n.t().personas.rationale}
										hint={i18n.t().personas.rationaleHint}
										required
										multiline
										problem={
											requirement().rationale.trim() === ""
												? i18n.t().personas.rationaleRequired
												: undefined
										}
										value={requirement().rationale}
										onInput={(rationale) =>
											setRequirementDraft({ ...requirement(), rationale })
										}
									/>
									<TextField
										label={i18n.t().common.author}
										value={requirement().author}
										onInput={(author) =>
											setRequirementDraft({ ...requirement(), author })
										}
									/>
									<Toggle
										label={i18n.t().common.active}
										checked={requirement().active}
										onToggle={(active) =>
											setRequirementDraft({ ...requirement(), active })
										}
									/>
									<Actions>
										<Button
											label={i18n.t().common.create}
											tone="primary"
											disabled={requirement().rationale.trim() === ""}
											onClick={() => void saveRequirement(requirement())}
										/>
										<Button
											label={i18n.t().common.cancel}
											onClick={() => setRequirementDraft(undefined)}
										/>
									</Actions>
								</Form>
							)}
						</Async>
					</Panel>
				)}
			</Show>
		</>
	);
};
